import { useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import Modal from '../modal/Modal'
import { Button } from '../generic/Core'
import useTranslation from '../../hooks/useTranslation'

/**
 * The export dialog: scope, format, and columns (task 35.01 §8).
 *
 * Composed the same way PopUpModal composes it — Modal wrapping Header, Body and
 * Footer. Modal.Dialog and Modal.Content are Modal's own internals and must not
 * be nested here, or the dialog renders as a second card inside the first.
 *
 * The choices come from the shared export model rather than from anything
 * written here: `formats` from formatData, `profiles` from exportData. Adding a
 * format to that registry puts it in this dialog with no change to this file.
 *
 * The segmented groups mirror SwitchSelect's look deliberately. SwitchSelect
 * itself cannot be reused: it fetches its options from `/{entity}`, and formats
 * and profiles are local config, not a dataCollection.
 */

const Field = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const FieldLabel = styled.div`
  font-size: var(--font-XS);
  color: ${({ theme }) => theme.font.label};
  margin-bottom: 0.4rem;
`

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: fit-content;
  max-width: 100%;
  gap: 0.375rem;
  padding: 0.25rem;
  border: 1px solid ${({ theme }) => theme.form.border};
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.form.textControl.background || theme.btn.grayBg};
`

const Option = styled.button`
  flex: 0 1 auto;
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${({ theme, $isActive }) => $isActive ? '#fff' : theme.font.color};
  font-size: var(--font-XS);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease-in-out;

  ${({ $isActive, theme }) => $isActive && `
    background-color: ${theme.btn.primary};
    color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `}

  &:hover:not(:disabled) {
    ${({ $isActive, theme }) => !$isActive && `
      background-color: ${theme.table.hover || 'rgba(0, 0, 0, 0.05)'};
    `}
  }

  &:focus { outline: none; }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const ExportModal = ({
    onClose,
    show = true,
    zIndex,
    formats = [],
    profiles = [],
    selectedCount = 0,
    onExport
}) => {
    const { t } = useTranslation()

    const [format, setFormat] = useState(formats[0]?.id || 'csv')
    const [profile, setProfile] = useState(profiles[0]?.name || 'default')
    // Default to the selection when there is one — that is what the user just did.
    const [scope, setScope] = useState(selectedCount > 0 ? 'selection' : 'all')
    const [busy, setBusy] = useState(false)

    const handleExport = async () => {
        setBusy(true)
        try {
            await onExport({ format, profile, scope })
            onClose?.()
        } finally {
            setBusy(false)
        }
    }

    return ReactDOM.createPortal(
        <Modal zIndex={zIndex} show={show} onClose={onClose} setShow={onClose}>
            <Modal.Header onClose={onClose}>
                <h4>{t('export.modal.title')}</h4>
            </Modal.Header>
            <Modal.Body>
                <Field>
                    <FieldLabel>{t('export.modal.scope')}</FieldLabel>
                    <Options>
                        <Option
                            type="button"
                            $isActive={scope === 'selection'}
                            disabled={selectedCount === 0}
                            onClick={() => setScope('selection')}
                        >
                            {selectedCount > 0
                                ? `${t('export.modal.scope.selection')} (${selectedCount})`
                                : t('export.modal.scope.selection')}
                        </Option>
                        <Option
                            type="button"
                            $isActive={scope === 'all'}
                            onClick={() => setScope('all')}
                        >
                            {t('export.modal.scope.all')}
                        </Option>
                    </Options>
                </Field>

                <Field>
                    <FieldLabel>{t('export.modal.format')}</FieldLabel>
                    <Options>
                        {formats.map(item => (
                            <Option
                                key={item.id}
                                type="button"
                                $isActive={format === item.id}
                                onClick={() => setFormat(item.id)}
                            >
                                {t(item.label)}
                            </Option>
                        ))}
                    </Options>
                </Field>

                {profiles.length > 0 && (
                    <Field>
                        <FieldLabel>{t('export.modal.profile')}</FieldLabel>
                        <Options>
                            {profiles.map(item => (
                                <Option
                                    key={item.name}
                                    type="button"
                                    $isActive={profile === item.name}
                                    onClick={() => setProfile(item.name)}
                                >
                                    {t(item.label)}
                                </Option>
                            ))}
                        </Options>
                    </Field>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" $variant="secondary" onClick={onClose}>
                    {t('all.confirm.cancel')}
                </Button>
                <Button type="button" $variant="primary" disabled={busy} onClick={handleExport}>
                    {busy ? t('export.modal.working') : t('export.modal.accept')}
                </Button>
            </Modal.Footer>
        </Modal>,
        document.body
    )
}

export default ExportModal
